/**
 * Every toolbar item as the Settings panel needs to see it: a label, an icon
 * and its anchoring rules, with no connection or engine in the picture.
 *
 * Built from the SAME descriptor builders the toolbar renders from, with a
 * deliberately permissive gating object, so the customization list can never
 * drift from the toolbar's own item set — adding an item to the toolbar adds
 * it here, and `toolbarItemCatalog.test.ts` pins that the two agree.
 */
import type { LucideIcon } from 'lucide-react';
import {
  DEFAULT_TOOLBAR_LAYOUT,
  type ToolbarActionId,
  type ToolbarActions,
  type ToolbarGating,
  type ToolbarItemId,
  type ToolbarMoreGroupId,
} from './toolbarItemTypes';
import { isToolbarItemGateOpen, TOOLBAR_HOME_GROUP } from './mergeToolbarLayout';
import { allBetaFeaturesEnabled } from '../../../core/beta';
import { buildPrimaryToolbarItems } from './toolbarPrimaryItems';
import { buildMoreToolbarItems } from './toolbarMoreItems';
import { buildDbaToolbarItems } from './toolbarDbaItems';

/** Everything on, so the catalog holds every item regardless of context. */
export const NEUTRAL_TOOLBAR_GATING: ToolbarGating = {
  contextState: { hasConnection: true, hasDatabase: true },
  firstConnectCoachVisible: false,
  browserMode: false,
  hasFallbackConnection: true,
  notebooksUnsupported: false,
  schemaOverviewUnsupported: false,
  spatialExplorerUnsupported: false,
  importDataUnsupportedReason: undefined,
  queryEditorUnsupported: false,
  tableExplorerUnsupported: false,
  hasDbaEligibleConnection: true,
  hasAnyOracleConnection: true,
  hasAnyMssqlConnection: true,
  hasJobScheduler: true,
  jobSchedulerMenuLabel: 'Agent Jobs',
  betaEnabled: allBetaFeaturesEnabled(),
};

const ALL_IDS: ToolbarItemId[] = [
  ...DEFAULT_TOOLBAR_LAYOUT.primary,
  ...DEFAULT_TOOLBAR_LAYOUT.more.flatMap((group) => group.items),
];

const NOOP_ACTIONS = Object.fromEntries(
  ALL_IDS.filter((id): id is ToolbarActionId => id !== 'ai' && id !== 'more').map((id) => [
    id,
    () => {},
  ]),
) as ToolbarActions;

/**
 * A handful of toolbar labels are abbreviated to fit the bar ("New",
 * "Explorer", "Overview") or are two different entries sharing one word. The
 * settings list has room for the unambiguous name.
 */
const SETTINGS_LABELS: Partial<Record<ToolbarItemId, string>> = {
  'new-query': 'New Query',
  'table-explorer': 'Table Explorer',
  'table-explorer-more': 'Table Explorer',
  'schema-overview': 'Schema Overview',
  'table-designer': 'Table Designer',
  'data-profile': 'Data Profile',
  ai: 'AI menu',
  more: 'More ▾ menu',
};

export type ToolbarCatalogEntry = {
  id: ToolbarItemId;
  label: string;
  icon?: LucideIcon;
  /** `false` for the anchors — the panel offers no hide control for them. */
  hideable: boolean;
  anchor?: 'first' | 'last';
  /** Where the item lives in the More menu, when it lives there at all. */
  group?: ToolbarMoreGroupId;
};

const deps = { gating: NEUTRAL_TOOLBAR_GATING, actions: NOOP_ACTIONS };

export const TOOLBAR_ITEM_CATALOG: ReadonlyMap<ToolbarItemId, ToolbarCatalogEntry> = new Map(
  [
    ...buildPrimaryToolbarItems(deps),
    ...buildMoreToolbarItems(deps),
    ...buildDbaToolbarItems(deps),
  ].map((item) => [
    item.id,
    {
      id: item.id,
      label: SETTINGS_LABELS[item.id] ?? item.label,
      icon: item.icon,
      hideable: item.hideable !== false,
      anchor: item.anchor,
      group: TOOLBAR_HOME_GROUP.get(item.id),
    },
  ]),
);

export function toolbarCatalogEntry(id: ToolbarItemId): ToolbarCatalogEntry {
  return (
    TOOLBAR_ITEM_CATALOG.get(id) ?? { id, label: id, hideable: true }
  );
}

/**
 * Items that would NOT render right now, for the connections currently open.
 *
 * The catalog is deliberately gate-blind so Settings can list everything, but
 * that leaves ~10 engine-gated items a user can tick with nothing appearing on
 * the toolbar afterwards — and the checkbox stays ticked, so it reads as a
 * broken control rather than an unmet precondition. The panel annotates these
 * rows instead of hiding or disabling them: the gate is a property of the
 * connection, not of the choice, so the choice is still worth recording for
 * the next time an Oracle connection is open.
 */
export function unavailableToolbarItems(gating: ToolbarGating): Set<ToolbarItemId> {
  const live = { gating, actions: NOOP_ACTIONS };
  const out = new Set<ToolbarItemId>();
  for (const item of [
    ...buildPrimaryToolbarItems(live),
    ...buildMoreToolbarItems(live),
    ...buildDbaToolbarItems(live),
  ]) {
    if (item.visible === false || !isToolbarItemGateOpen(item.id, gating)) out.add(item.id);
  }
  return out;
}
